import React from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  DividerProps,
  IconProps,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
  darken,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { getSubjectColorNew, getUrlConvertedString } from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';

import {
  IGroupedSubTopic,
  IGroupedTopic,
} from '../../../../store/SubTopicSlice';
import { getProgressState } from '../../../../store/ProgressSlice';

const mainBoxStyles: BoxProps = {
  position: 'sticky',
  top: '80px',
  zIndex: 5,
};

const subTopicBoxProps: BoxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
};

const checkIconProps: IconProps['sx'] = {
  fontSize: 'small',
  style: {
    marginRight: '8px',
  },
};

const dividerProps: DividerProps = {
  flexItem: true,
  orientation: 'vertical',
  variant: 'middle',
};

const quizTypoProps: TypographyProps = {
  variant: 'body2',
  padding: '6px',
  sx: { textDecoration: 'underline', opacity: 0.5 },
};

export const checkResources = (subTopic: IGroupedSubTopic): boolean =>
  !!(subTopic.resources && subTopic.resources.length);

export const checkVideos = (subTopic: IGroupedSubTopic): boolean =>
  !!(subTopic.videoLinks && subTopic.videoLinks.length);

export const checkTopicPlanner = (subTopicName: string): boolean =>
  subTopicName.toLocaleLowerCase().search('topic planner') < 0;

export const checkQuizzes = (subTopic: IGroupedSubTopic): boolean =>
  !!(subTopic.quizCount && subTopic.quizCount > 0);

interface SubTopicListProps {
  selectedTopic: IGroupedTopic;
}

const SubTopicList: React.FC<SubTopicListProps> = ({ selectedTopic }) => {
  const navigate = useNavigate();

  // Store
  const progressState = useAppSelector(getProgressState);

  const { subjectProgress } = progressState;

  const subjectColor = getSubjectColorNew(selectedTopic.subject);
  const urlConvertedCurriculum = getUrlConvertedString(
    selectedTopic.curriculum
  );
  const urlConvertedSubject = getUrlConvertedString(selectedTopic.subject);
  const urlConvertedTopic = getUrlConvertedString(selectedTopic.name);

  const handleQuizButtonClick = (subTopicData: IGroupedSubTopic) => {
    const urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);

    navigate(
      `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData._id}/quiz`
    );
  };

  const handleButtonClick = (
    subTopicData: IGroupedSubTopic,
    type?: 'resources' | 'videos'
  ) => {
    const urlConvertedSubTopic = getUrlConvertedString(
      subTopicData?.name || ''
    );

    let _url = `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData._id}`;

    if (type === 'videos') {
      _url += `?toVideos=1`;
    }

    navigate(_url);
  };

  const getSubTopicProgress = (topicId: string, subTopicId: string) => {
    let isCompleted = false;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicId
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const subTopicProgress = topicProgress.subTopics.find(
          (item) => item._id === subTopicId
        );

        if (subTopicProgress && subTopicProgress.completion === 100) {
          isCompleted = true;
        }
      }
    }
    return isCompleted;
  };

  const textButtonProps: ButtonProps = {
    variant: 'text',
    sx: { color: subjectColor, textDecoration: 'underline' },
  };

  return (
    <Stack
      padding="20px"
      marginLeft="20px"
      sx={{
        width: { sm: '50%', md: '40%', lg: '35%' },
        display: { xs: 'none', sm: 'flex' },
        backgroundColor: `${subjectColor}0A`,
        borderRadius: '20px',
      }}
    >
      <Box sx={mainBoxStyles}>
        <Box {...subTopicBoxProps}>
          <Typography variant="h6" color={TP_COLORS.gray.dark}>
            {selectedTopic.name}
          </Typography>
          <Button
            color="secondary"
            size="small"
            variant="contained"
            sx={{
              minWidth: '70px',
              backgroundColor: subjectColor,
              '&:hover': {
                backgroundColor: darken(subjectColor, 0.3),
              },
            }}
            onClick={() =>
              navigate(`${urlConvertedTopic}/${selectedTopic._id}`)
            }
          >
            View all
          </Button>
        </Box>
        {selectedTopic.subTopics && selectedTopic.subTopics.length ? (
          <Stack>
            {selectedTopic.subTopics.map((subTopicItem, subTopicIndex) => (
              <Box key={subTopicIndex} display="flex" alignItems="center">
                {getSubTopicProgress(selectedTopic._id, subTopicItem._id) ? (
                  <CheckCircleIcon
                    sx={{ color: subjectColor }}
                    {...checkIconProps}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{ color: subjectColor }}
                    {...checkIconProps}
                  />
                )}

                <Stack
                  padding="8px"
                  sx={{
                    width: '85%',
                    borderRadius: '10px',
                    '&:hover': {
                      backgroundColor: `${subjectColor}1A`,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    paddingLeft="8px"
                    color={TP_COLORS.gray.dark}
                  >
                    {subTopicItem.name}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    {checkResources(subTopicItem) && (
                      <Button
                        {...textButtonProps}
                        onClick={() =>
                          handleButtonClick(subTopicItem, 'resources')
                        }
                      >
                        Resources
                      </Button>
                    )}

                    {checkTopicPlanner(subTopicItem.name) &&
                      checkResources(subTopicItem) && (
                        <Divider {...dividerProps} />
                      )}

                    {checkTopicPlanner(subTopicItem.name) && (
                      <>
                        {checkVideos(subTopicItem) && (
                          <>
                            <Button
                              {...textButtonProps}
                              onClick={() =>
                                handleButtonClick(subTopicItem, 'videos')
                              }
                            >
                              Videos
                            </Button>
                            <Divider {...dividerProps} />
                          </>
                        )}

                        {checkQuizzes(subTopicItem) ? (
                          <Button
                            {...textButtonProps}
                            onClick={() => handleQuizButtonClick(subTopicItem)}
                          >
                            Quizzes
                          </Button>
                        ) : (
                          <Tooltip
                            title="Content Upcoming"
                            placement="right-start"
                          >
                            <Typography {...quizTypoProps}>Quizzes</Typography>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : null}
      </Box>
    </Stack>
  );
};

export default SubTopicList;
